import { useSyncExternalStore } from 'react';

/** Player choices for sound and motion, remembered in localStorage. */
export interface AvPrefs {
  /** the start prompt has been answered */
  decided: boolean;
  sound: boolean;
  motion: boolean;
  /** iOS granted DeviceOrientation access this session */
  gyroGranted: boolean;
  music: number;
  sfx: number;
  voice: number;
}

const KEY = 'plotjack.av.v1';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

type OrientationCtor = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<'granted' | 'denied'> };
const orientationCtor = () => (typeof window !== 'undefined' ? (window.DeviceOrientationEvent as OrientationCtor | undefined) : undefined);
/** iOS Safari gates the gyroscope behind a permission prompt that needs a user gesture. */
export const needsGyroPermission = () => typeof orientationCtor()?.requestPermission === 'function';

export const isTouchDevice = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1));

export type MotionKind = 'ios' | 'phone' | 'desktop';
export function motionKind(): MotionKind {
  if (isIOS()) return 'ios';
  if (isTouchDevice()) return 'phone';
  return 'desktop';
}

function load(): AvPrefs {
  const base: AvPrefs = { decided: false, sound: false, motion: !prefersReducedMotion(), gyroGranted: !needsGyroPermission(), music: 0.6, sfx: 0.8, voice: 1 };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<AvPrefs>;
      // iOS asks again each session, so a stored grant is not trusted
      return { ...base, ...p, gyroGranted: base.gyroGranted };
    }
  } catch {
    /* private mode */
  }
  return base;
}

let prefs: AvPrefs = typeof window === 'undefined' ? ({} as AvPrefs) : load();
const subs = new Set<() => void>();

export function getPrefs(): AvPrefs {
  return prefs;
}

export function setPrefs(patch: Partial<AvPrefs>) {
  prefs = { ...prefs, ...patch };
  try {
    const { gyroGranted: _g, ...persist } = prefs; // eslint-disable-line @typescript-eslint/no-unused-vars
    localStorage.setItem(KEY, JSON.stringify(persist));
  } catch {
    /* ignore */
  }
  subs.forEach((f) => f());
}

export function useAvPrefs(): AvPrefs {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => prefs,
    () => prefs,
  );
}

/**
 * Ask iOS for gyroscope access. Must be called synchronously inside a tap handler (before any await),
 * so call it first in the handler. Resolves to whether tilt can be used.
 */
export function requestGyro(): Promise<boolean> {
  const ctor = orientationCtor();
  // desktops look around with the mouse, so they never need the sensor prompt
  if (!isTouchDevice() || typeof ctor?.requestPermission !== 'function') return Promise.resolve(true);
  return ctor
    .requestPermission()
    .then((r) => {
      const ok = r === 'granted';
      setPrefs({ gyroGranted: ok });
      return ok;
    })
    .catch(() => {
      setPrefs({ gyroGranted: false });
      return false;
    });
}
